import { useQueryClient, QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useModals } from "../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { initialBenefitsFetch } from "../../../permissions/benefits/redux/benefits.init";
import { resetConnectBusinessState } from "../../../redux/features/connectBusinessStateSlice";
import { resetMarkerCount } from "../../../redux/features/coverageSlice";
import { clearScratchCreateAutoFlow } from "../../../redux/features/jobCreateSlice";
import { setPlanSelected } from "../../../redux/features/planStateSlice";
import { selectActiveCompanyId, selectUserCompanies } from "../../../redux/features/user/selectors/userState.selectors";
import { setActiveCompany, updateUser } from "../../../redux/features/userStateSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/redux";
import { useOptionalChatDock } from "../../chat-dock/ChatDockContext";
import { CompanySwitchSnackbarContent } from "../../common/CompanySwitchSnackbarContent";
import { useSwitchCompanyMutation } from "./useCompany";

const COMPANY_AGNOSTIC_QUERY_KEYS = new Set(["company-numbers-by-companies"]);

export const resetCompanyScopedQueries = (queryClient: QueryClient) =>
  queryClient.resetQueries({
    predicate: (query) => !COMPANY_AGNOSTIC_QUERY_KEYS.has(String(query.queryKey?.[0])),
  });

interface SwitchActiveCompanyOptions {
  notify?: boolean;
}

export const useSwitchActiveCompany = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const companies = useAppSelector(selectUserCompanies);
  const activeCompanyId = useAppSelector(selectActiveCompanyId);
  const { prepareModalDockForCompanySwitch } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const switchCompanyMutation = useSwitchCompanyMutation();
  const chatDock = useOptionalChatDock();

  const switchActiveCompany = useCallback(
    async (companyId: number | string, { notify = true }: SwitchActiveCompanyOptions = {}) => {
      chatDock?.saveOpenChatsForCompany(activeCompanyId);

      const { token, user: userData } = await switchCompanyMutation.mutateAsync(Number(companyId));

      sessionStorage.setItem("token", token);
      dispatch(updateUser(userData));
      dispatch(setActiveCompany(companyId));
      dispatch(initialBenefitsFetch());
      dispatch(resetConnectBusinessState());
      dispatch(resetMarkerCount());
      dispatch(clearScratchCreateAutoFlow());
      dispatch(setPlanSelected({ upgradeFreeTrial: false }));
      prepareModalDockForCompanySwitch?.();
      chatDock?.restoreOpenChatsForCompany(companyId);

      await resetCompanyScopedQueries(queryClient);

      const membership: any = (companies ?? []).find(
        (item: any) => String(item?.companyId ?? item?.company?.id ?? item?.id) === String(companyId),
      );

      if (notify) {
        showSnackbar(
          <CompanySwitchSnackbarContent company={membership?.company ?? membership} />,
          "success",
          5000,
          "top-left",
        );
      }

      return membership;
    },
    [activeCompanyId, chatDock, companies, dispatch, prepareModalDockForCompanySwitch, queryClient, showSnackbar, switchCompanyMutation],
  );

  return {
    switchActiveCompany,
    isSwitchingCompany: switchCompanyMutation.isPending,
    switchingCompanyId: switchCompanyMutation.variables,
  };
};
